import { api, ApiError, explain } from './api';
import type { AnalysisVariation, ResultsView, ShareStatus } from './api';
import { lineChart, lineStyle } from './chart';
import { alertBox, clear, h, statusBox } from './dom';
import {
  ADVICE,
  TIERS,
  chance,
  headlineFor,
  money,
  num,
  pct,
  progressText,
  range,
  signedPct,
} from './format';
import type { Tier } from './format';
import { sectionLine, sectionsHeadline, shipAdvice } from './mvt-text';
import { navigate } from './router';
import type { Ctx } from './pages';

const REFRESH_MS = 60_000;

const th = (text: string) => h('th', { scope: 'col' }, text);
const td = (...c: Array<Node | string>) => h('td', {}, ...c);

function table(caption: string, head: string[], rows: Array<Array<Node | string>>): HTMLElement {
  return h(
    'div',
    { class: 'scroll' },
    h(
      'table',
      {},
      h('caption', { class: 'vh' }, caption),
      h('thead', {}, h('tr', {}, ...head.map(th))),
      h('tbody', {}, ...rows.map((r) => h('tr', {}, ...r.map((c) => td(c))))),
    ),
  );
}

/** The verdict first: one sentence, what it means, and how far along the test is. */
function verdictCard(v: ResultsView): HTMLElement {
  const a = v.analysis!;
  // a multivariate test is judged section by section; progress is that of its slowest section
  const mv =
    v.factors && a.factors && a.factorsOverall
      ? { sections: a.factors, defs: v.factors, overall: a.factorsOverall }
      : null;
  const tier = (mv ? mv.overall.tier : a.verdict.tier) as Tier;
  const t = TIERS[tier];
  const slowest = mv
    ? mv.sections.reduce((m, s) => (s.verdict.progress < m.verdict.progress ? s : m))
    : a;
  const lowest = Math.min(...(mv ? slowest.variations : v.variations).map((x) => x.visitors));
  const optionsInSlowest = mv ? slowest.variations.length : v.variations.length;
  const prog = {
    progress: slowest.verdict.progress,
    planned: slowest.verdict.plannedPerVariation,
    lowest,
    remaining:
      v.forecast.visitorsPerDay > 0 && slowest.verdict.plannedPerVariation > 0
        ? Math.max(
            0,
            Math.ceil(
              (slowest.verdict.plannedPerVariation - lowest) /
                (v.forecast.visitorsPerDay / Math.max(1, optionsInSlowest)),
            ),
          )
        : null,
  };
  const card = h(
    'section',
    { class: `verdict ${t.tone}`, 'aria-labelledby': 'verdict-h', 'data-tier': tier },
    h('p', { class: 'tier' }, t.label),
    h(
      'h2',
      { id: 'verdict-h' },
      mv
        ? sectionsHeadline(tier, mv.overall.recommended, mv.defs)
        : headlineFor(tier, a.verdict.winner, v.variations),
    ),
    h('p', {}, ADVICE[tier]),
  );
  if (mv && tier === 'confirmed') {
    const todo = shipAdvice(mv.overall.recommended, mv.defs);
    if (todo) card.append(h('p', { class: 'ship' }, todo));
  }
  if (tier !== 'check_data') {
    card.append(
      h(
        'p',
        { class: 'muted' },
        progressText(
          prog.progress,
          prog.planned,
          prog.lowest,
          prog.remaining,
          a.verdict.config.mdeRelative ?? 0.2,
        ),
      ),
      h(
        'div',
        {
          class: 'bar',
          role: 'progressbar',
          'aria-label': 'Progress towards the planned sample',
          'aria-valuemin': '0',
          'aria-valuemax': '100',
          'aria-valuenow': String(Math.round(prog.progress * 100)),
        },
        h('span', { style: `width:${Math.round(prog.progress * 100)}%` }),
      ),
    );
    const reasons = mv
      ? mv.sections.flatMap((s) => {
          const def = mv.defs.find((d) => d.id === s.id);
          return s.variations
            .filter((x) => !x.isControl && x.vsControl && x.vsControl.tier !== 'confirmed')
            .map(
              (x) =>
                `${def?.name ?? s.id}: ${def?.options.find((o) => o.id === x.id)?.name ?? x.id}: ${x.vsControl!.reason}`,
            );
        })
      : a.variations
          .filter((x) => !x.isControl && x.vsControl && x.vsControl.tier !== 'confirmed')
          .map((x) => `${nameOf(v, x.id)}: ${x.vsControl!.reason}`);
    if (reasons.length && tier !== 'no_meaningful_difference')
      card.append(h('ul', { class: 'reasons' }, ...reasons.map((r) => h('li', {}, r))));
  }
  return card;
}

const nameOf = (v: ResultsView, id: string) => v.variations.find((x) => x.id === id)?.name ?? id;

function warnings(v: ResultsView): HTMLElement | null {
  const items: HTMLElement[] = [];
  const a = v.analysis;
  if (a?.srm?.mismatch)
    items.push(
      h(
        'div',
        { role: 'alert', class: 'warning err', 'data-warning': 'srm' },
        h('strong', {}, 'Check your data. '),
        'Visitors were not split between the versions the way they should have been (the chance of a split this uneven by luck is about ',
        a.srm.pValue < 0.0001 ? 'under 1 in 10,000' : `${a.srm.pValue.toPrecision(2)}`,
        '). Common causes: a redirect that loses some visitors, a script error in one version, or a page that blocks the snippet. Until it is fixed, treat every number here as unreliable.',
      ),
    );
  for (const hw of v.health ?? [])
    items.push(
      h('div', { role: 'alert', class: 'warning warn', 'data-warning': 'health' }, hw.message),
    );
  if (v.revenueTruncated)
    items.push(
      h(
        'div',
        { role: 'status', class: 'warning warn' },
        'This experiment has more paying visitors than can be analysed at once, so revenue figures use the earliest ones.',
      ),
    );
  if (v.unavailable === 'stats_unavailable')
    items.push(
      h(
        'div',
        { role: 'alert', class: 'warning warn', 'data-warning': 'stats' },
        'The statistics could not be calculated just now. The visitor counts below are current; try again in a minute.',
      ),
    );
  return items.length ? h('div', { class: 'warnings' }, ...items) : null;
}

function resultsTable(
  list: AnalysisVariation[],
  nameFor: (id: string) => string,
  caption = 'Results by variation',
  first = 'Variation',
): HTMLElement {
  const rows = list.map((x) => {
    const vs = x.vsControl;
    return [
      h('strong', {}, nameFor(x.id)),
      num(x.visitors),
      num(x.converters),
      x.rate === null ? '' : pct(x.rate, 2),
      x.isControl
        ? h('span', { class: 'muted' }, 'the original')
        : vs?.lift == null
          ? h('span', { class: 'muted' }, 'not enough data')
          : h(
              'span',
              {},
              signedPct(vs.lift),
              ' ',
              h('span', { class: 'muted' }, vs.liftInterval ? `(${range(vs.liftInterval)})` : ''),
            ),
      x.isControl ? '' : chance(vs?.probBeatsControl),
      x.probBest === undefined ? '' : chance(x.probBest),
    ];
  });
  return table(
    caption,
    [
      first,
      'Visitors',
      'Conversions',
      'Conversion rate',
      'Compared with the original',
      'Chance to beat the original',
      'Chance of being the best',
    ],
    rows,
  );
}

function revenueTable(v: ResultsView): HTMLElement | null {
  const a = v.analysis!;
  if (!a.variations.some((x) => x.revenue)) return null;
  const rows = a.variations.map((x) => {
    const r = x.revenue as
      | {
          perVisitor: number;
          total: number;
          payers: number;
          capped: number;
          vsControl?: { diff: number; diffInterval: [number, number] };
        }
      | undefined;
    return [
      h('strong', {}, nameOf(v, x.id)),
      r ? num(r.payers) : '',
      r ? money(r.total) : '',
      r ? money(r.perVisitor) : '',
      r?.vsControl
        ? `${r.vsControl.diff >= 0 ? '+' : '-'}${money(Math.abs(r.vsControl.diff))} (${money(r.vsControl.diffInterval[0])} to ${money(r.vsControl.diffInterval[1])})`
        : x.isControl
          ? h('span', { class: 'muted' }, 'the original')
          : '',
    ];
  });
  const cap = (a.variations.find((x) => x.revenue)?.revenue as { cap?: number | null } | undefined)
    ?.cap;
  return h(
    'div',
    {},
    h('h2', {}, 'Revenue'),
    table(
      'Revenue by variation',
      [
        'Variation',
        'Paying visitors',
        'Total revenue',
        'Revenue per visitor',
        'Difference per visitor',
      ],
      rows,
    ),
    h(
      'p',
      { class: 'muted' },
      'Revenue per visitor counts visitors who did not buy as zero. ',
      cap != null
        ? `To stop one huge order deciding the result, any single order above ${money(cap)} is counted as ${money(cap)}. `
        : '',
      'The verdict above is based on conversions, not revenue.',
    ),
  );
}

/** A multivariate test: one block per section, each with its own verdict line and options table. */
function sectionsBlock(v: ResultsView): HTMLElement[] {
  const a = v.analysis!;
  const out: HTMLElement[] = [h('h2', {}, 'By section')];
  out.push(
    h(
      'p',
      { class: 'muted' },
      'Each section is judged on every visitor who saw one of its options, whatever the other sections showed.',
    ),
  );
  for (const sec of a.factors ?? []) {
    const def = v.factors!.find((f) => f.id === sec.id);
    if (!def) continue;
    const tier = sec.verdict.tier as Tier;
    const name = (id: string) => def.options.find((o) => o.id === id)?.name ?? id;
    out.push(
      h(
        'section',
        {
          class: `section-result ${TIERS[tier].tone}`,
          'aria-label': `Section ${def.name}`,
          'data-section': def.name,
          'data-tier': tier,
        },
        h('h3', {}, def.name),
        h(
          'p',
          { class: 'section-line' },
          h('strong', {}, TIERS[tier].label),
          ' ',
          sectionLine(tier, sec.verdict.winner, def),
        ),
        resultsTable(sec.variations, name, `Results for the section ${def.name}`, 'Option'),
      ),
    );
  }
  out.push(
    h(
      'p',
      { class: 'muted' },
      'These results assume the sections do not strongly depend on each other. The combinations table further down shows every combination, if you want to look for exceptions.',
    ),
  );
  return out;
}

function chart(v: ResultsView): HTMLElement | null {
  if (v.daily.days.length < 1) return null;
  if (v.factors) {
    // one chart per section, a line per option: visitors of every combination showing that option
    const wrap = h('div', {}, h('h2', {}, 'Conversion rate over time, by section'));
    for (const f of v.factors) {
      const lines = f.options.map((o) => {
        const members = v.variations.filter((x) => x.levels?.[f.id] === o.id);
        return {
          name: o.name,
          values: v.daily.days.map((_, i) => {
            let vis = 0;
            let conv = 0;
            for (const m of members) {
              vis += v.daily.series[m.id]?.visitors[i] ?? 0;
              conv += v.daily.series[m.id]?.converters[i] ?? 0;
            }
            return vis > 0 ? conv / vis : null;
          }),
        };
      });
      wrap.append(h('h3', {}, f.name), ...lineChartWithLegend(v.daily.days, lines));
    }
    return wrap;
  }
  const lines = v.variations.map((x) => {
    const s = v.daily.series[x.id];
    return {
      name: x.name,
      values: v.daily.days.map((_, i) =>
        s && s.visitors[i]! > 0 ? s.converters[i]! / s.visitors[i]! : null,
      ),
    };
  });
  return h(
    'div',
    {},
    h('h2', {}, 'Conversion rate over time'),
    h(
      'p',
      { class: 'muted' },
      'Running total since the first visitor: how many visitors converted, by day.',
    ),
    ...lineChartWithLegend(v.daily.days, lines),
  );
}

function lineChartWithLegend(
  days: string[],
  lines: Array<{ name: string; values: Array<number | null> }>,
): Node[] {
  const legend = h(
    'ul',
    { class: 'legend' },
    ...lines.map((l, i) => {
      const st = lineStyle(i);
      const sw = h('span', { class: 'swatch', 'aria-hidden': 'true' });
      sw.style.borderTopColor = st.color;
      sw.style.borderTopStyle = st.dash ? 'dashed' : 'solid';
      return h('li', {}, sw, l.name);
    }),
  );
  return [lineChart({ days, lines, format: (x) => pct(x, x < 0.1 ? 2 : 1) }), legend];
}

/** The whole results view: verdict, warnings, goal choice, table, chart. */
export function renderResults(
  v: ResultsView,
  opts: { onGoal: (goalId: string) => void; extra?: HTMLElement | null },
): HTMLElement {
  const root = h('div', { class: 'results' });
  const w = warnings(v);

  if (v.analysis) root.append(verdictCard(v));
  else
    root.append(
      h(
        'section',
        { class: 'verdict muted', 'data-tier': 'none' },
        h('p', { class: 'tier' }, 'No verdict yet'),
        h(
          'h2',
          {},
          v.unavailable === 'no_goal'
            ? 'This experiment has no goal.'
            : v.unavailable === 'no_data'
              ? 'No visitors have been counted yet.'
              : 'The statistics are not available right now.',
        ),
        h(
          'p',
          {},
          v.unavailable === 'no_goal'
            ? 'A goal says what counts as success (a click, a page view, a purchase). Add one to the experiment to see results.'
            : v.unavailable === 'no_data'
              ? v.experiment.status === 'draft'
                ? 'Start the experiment, and make sure the install snippet is on the site. Results appear as visitors arrive.'
                : 'Results appear as visitors arrive. If this stays empty, check that the install snippet is on the page.'
              : 'Visitor counts are shown below.',
        ),
      ),
    );
  if (w) root.append(w);

  if (v.goals.length > 1) {
    const sel = h('select', { 'aria-label': 'Goal' }) as HTMLSelectElement;
    for (const g of v.goals) {
      const o = h('option', { value: g.id }, g.name);
      if (g.id === v.goalId) o.selected = true;
      sel.append(o);
    }
    sel.addEventListener('change', () => opts.onGoal(sel.value));
    root.append(
      h(
        'p',
        { class: 'row' },
        h('label', { class: 'field' }, h('span', { class: 'lbl' }, 'Goal'), sel),
      ),
    );
  } else if (v.goals[0]) {
    root.append(h('p', { class: 'muted' }, `Goal: ${v.goals[0].name}`));
  }

  if (v.analysis) {
    root.append(...(v.factors && v.analysis.factors ? sectionsBlock(v) : []));
    root.append(
      h('h2', {}, v.factors ? 'All combinations' : 'Results'),
      resultsTable(v.analysis.variations, (id) => nameOf(v, id)),
    );
    const rev = revenueTable(v);
    if (rev) root.append(rev);
  } else if (v.variations.some((x) => x.visitors > 0)) {
    root.append(
      h('h2', {}, 'Visitors'),
      table(
        'Visitors by variation',
        ['Variation', 'Visitors'],
        v.variations.map((x) => [h('strong', {}, x.name), num(x.visitors)]),
      ),
    );
  }
  const c = chart(v);
  if (c && v.analysis) root.append(c);
  if (opts.extra) root.append(opts.extra);
  if (v.startedAt)
    root.append(
      h(
        'p',
        { class: 'muted' },
        `First visitor counted ${v.startedAt.slice(0, 10)}; ${Math.floor(v.days)} full day${Math.floor(v.days) === 1 ? '' : 's'} of data.`,
      ),
    );
  return root;
}

// ---- the owner's page, with sharing ----------------------------------------------------------

function sharePanel(expId: string, ctx: Ctx): HTMLElement {
  const box = h('section', { 'aria-label': 'Sharing', class: 'share' });
  const msg = h('div', { role: 'status' });
  const body = h('div', {});
  box.append(h('h2', {}, 'Share these results'), body, msg);

  const draw = async () => {
    const st = await api<ShareStatus>('GET', `/experiments/${expId}/share`);
    if (ctx.signal.aborted) return;
    clear(body);
    body.append(
      h(
        'p',
        { class: 'muted' },
        'Anyone with the link can see these results (and nothing else). It never shows your account or settings.',
      ),
    );
    const make = h(
      'button',
      { type: 'button' },
      st.active ? 'Make a new link' : 'Create a read-only link',
    );
    make.addEventListener('click', async () => {
      make.disabled = true;
      clear(msg);
      try {
        const r = await api<{ url: string; expiresAt: string }>(
          'POST',
          `/experiments/${expId}/share`,
          {},
        );
        await draw();
        const input = h('input', {
          type: 'text',
          readonly: true,
          value: r.url,
          'aria-label': 'Read-only link',
          class: 'wide',
        }) as HTMLInputElement;
        const copy = h('button', { type: 'button' }, 'Copy link');
        copy.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(r.url);
            clear(msg);
            msg.append(statusBox('Link copied.'));
          } catch {
            input.select();
            clear(msg);
            msg.append(statusBox('Press Ctrl+C (or Cmd+C) to copy the link.'));
          }
        });
        body.append(
          h(
            'p',
            {},
            statusBox(
              `New link made. It works until ${r.expiresAt.slice(0, 10)}, and any earlier link has stopped working.`,
            ),
          ),
          h('p', { class: 'row' }, input, copy),
          h(
            'p',
            { class: 'muted' },
            'This is the only time the link is shown. Make a new one if you lose it.',
          ),
        );
      } catch (e) {
        msg.append(alertBox(explain(e)));
      } finally {
        make.disabled = false;
      }
    });
    body.append(h('p', { class: 'row' }, make));
    if (st.active) {
      const stop = h('button', { type: 'button', class: 'danger' }, 'Stop sharing');
      stop.addEventListener('click', async () => {
        stop.disabled = true;
        clear(msg);
        try {
          await api('DELETE', `/experiments/${expId}/share`, {});
          await draw();
          msg.append(statusBox('Sharing stopped. The link no longer works.'));
        } catch (e) {
          msg.append(alertBox(explain(e)));
          stop.disabled = false;
        }
      });
      body.insertBefore(
        h('p', {}, `A link is active until ${st.expiresAt?.slice(0, 10)}. `, stop),
        body.firstChild?.nextSibling ?? null,
      );
    }
  };
  void draw().catch((e) => {
    if (e instanceof ApiError && e.status === 401) return navigate('/login', true);
    msg.append(alertBox(explain(e)));
  });
  return box;
}

/** Keep the page fresh while it is open, without a spinner or losing the reader's place. */
function every(ctx: Ctx, fn: () => Promise<void>): void {
  const timer = setInterval(() => void fn().catch(() => {}), REFRESH_MS);
  ctx.signal.addEventListener('abort', () => clearInterval(timer), { once: true });
}

export async function resultsPage(ctx: Ctx, id: string): Promise<void> {
  const { root } = ctx;
  let goal: string | undefined;
  const view = h('div', {});
  const fetchView = () =>
    api<ResultsView>(
      'GET',
      `/experiments/${id}/results${goal ? `?goal=${encodeURIComponent(goal)}` : ''}`,
    );
  let first: ResultsView;
  try {
    first = await fetchView();
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      root.append(
        h('h1', {}, 'Experiment not found'),
        h('p', {}, h('a', { href: '/sites' }, 'Back to your sites')),
      );
      return;
    }
    throw e;
  }
  root.append(
    h('p', {}, h('a', { href: `/experiments/${id}` }, 'Back to the experiment')),
    h('h1', {}, first.experiment.name || 'Results'),
    h('p', {}, h('span', { class: `badge ${first.experiment.status}` }, first.experiment.status)),
    view,
    sharePanel(id, ctx),
  );
  const draw = (v: ResultsView) => {
    clear(view);
    view.append(
      renderResults(v, {
        onGoal: (g) => {
          goal = g;
          void fetchView()
            .then(draw)
            .catch(() => view.append(alertBox('Could not load that goal.')));
        },
      }),
    );
  };
  draw(first);
  every(ctx, async () => {
    const v = await fetchView();
    if (!ctx.signal.aborted) draw(v);
  });
}

// ---- the read-only page behind a share link -----------------------------------------------------

export async function sharedPage(ctx: Ctx, token: string): Promise<void> {
  const { root } = ctx;
  let goal: string | undefined;
  const view = h('div', {});
  const fetchView = () =>
    api<ResultsView>('GET', `/shared/${token}${goal ? `?goal=${encodeURIComponent(goal)}` : ''}`);
  let first: ResultsView;
  try {
    first = await fetchView();
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 400)) {
      root.append(
        h('h1', {}, 'This link is no longer available'),
        h(
          'p',
          {},
          'It may have been replaced, switched off or expired. Ask the person who sent it for a new one.',
        ),
      );
      return;
    }
    if (e instanceof ApiError && e.status === 429) {
      root.append(h('h1', {}, 'Too many requests'), h('p', {}, 'Please wait a minute and reload.'));
      return;
    }
    throw e;
  }
  root.append(
    h('p', { class: 'muted shared-note' }, 'Read-only results, shared with you.'),
    h('h1', {}, first.experiment.name || 'Results'),
    view,
  );
  const draw = (v: ResultsView) => {
    clear(view);
    view.append(
      renderResults(v, {
        onGoal: (g) => {
          goal = g;
          void fetchView()
            .then(draw)
            .catch(() => view.append(alertBox('Could not load that goal.')));
        },
      }),
    );
  };
  draw(first);
  every(ctx, async () => {
    const v = await fetchView();
    if (!ctx.signal.aborted) draw(v);
  });
}
